import { ENDPOINTS, useCreatePayload } from 'hammock';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { MultiseriesApi } from '@candu-ai/analytics/content-events';
import { FilterTypes, useAvaliableSegmentIds } from '@candu-ai/analytics/search';
import { QueryId, QueryParamUrlObject } from '@candu-ai/analytics/types';
import { createUseQuery, withDocumentNodeId } from '@candu-ai/analytics/utils';
import { useContentSegments } from 'dashboard/pages/Content/pages/Analytics/state';

interface NodeMetadata {
  documentId: string;
  nodeId: string;
  name: string;
}

export const useTooltipMultiseriesApi = (
  queryId: QueryId,
  queryParams: QueryParamUrlObject,
): MultiseriesApi => {
  const { get } = useCreatePayload(ENDPOINTS.analyticQueries);
  const contentSegments = useContentSegments();

  const byDocumentLookup = useMemo(
    () => Object.fromEntries(contentSegments.map((c) => [c.documentId, c.segmentId])),
    [contentSegments],
  );
  const namesPayload = get({
    id: queryId,
    query: queryParams,
  });
  const result = useQuery(createUseQuery(namesPayload));

  const hotspots = (result?.data?.rows || []) as NodeMetadata[];

  const [queryDocumentIds] = useState<Set<number> | null>(null);

  const allSegmentIds = useMemo(() => {
    return [...new Set((result?.data?.rows || []).map(({ segmentId }) => segmentId))];
  }, [result]);

  const avaliableSegmentIds = useAvaliableSegmentIds(allSegmentIds);

  const names = useMemo(() => {
    return Object.fromEntries(
      (hotspots || []).map(({ documentId, nodeId, name }) => [
        withDocumentNodeId(documentId, nodeId),
        name,
      ]),
    );
  }, [hotspots]);

  const seriesMetadata = useMemo(() => {
    if (!result) {
      return {};
    }
    const filterSegmentIds = (segmentId: string) => avaliableSegmentIds.indexOf(segmentId) > -1;

    const documentIdsInQueryAndFiltered = (result?.data?.rows || [])
      .filter((s: any) => filterSegmentIds(s.segmentId))
      .map(({ documentId }) => documentId);
    const hasMultipleSegments = new Set(documentIdsInQueryAndFiltered).size > 1;

    const entries = (result?.data?.rows || [])
      .filter((r: any) => filterSegmentIds(r.segmentId))
      .map(({ documentId, segmentName, nodeId, name }) => ({
        value: withDocumentNodeId(documentId, nodeId),
        label: hasMultipleSegments ? `${segmentName} | ${name}` : name,
        // don't let me filter if there is only one segment, it's already filtered
        filterType: hasMultipleSegments ? FilterTypes.SegmentId : undefined,
        filterValue: byDocumentLookup[documentId],
      }));

    return {
      entries,
      seriesIds: entries.map((e) => e.value),
    };
  }, [result?.data?.rows || [], byDocumentLookup, queryDocumentIds]);

  return {
    canShowMoreSeries(): boolean {
      return false;
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onShowAllSeries(): void {},
    hasLoaded: () => !!result?.data,

    getLabel: (id) => {
      return names[id];
    },
    getEntries: () => {
      return seriesMetadata?.entries || [];
    },
    getSeriesIds: () => {
      return seriesMetadata?.seriesIds || [];
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onQueriesHaveLoaded(): void {},
  };
};

export const useHotspotMultiseries = (queryParams: QueryParamUrlObject): MultiseriesApi =>
  useTooltipMultiseriesApi(QueryId.HotspotNames, queryParams);

export const useTourMultiseries = (queryParams: QueryParamUrlObject): MultiseriesApi =>
  useTooltipMultiseriesApi(QueryId.TourStepNames, queryParams);
